import React, { useMemo } from "react";
import { TagRecord } from "../../types/appointment";
import { track } from "../../utils/tracking";

export type AptTagsPanelProps = {
  allAppointmentTags: TagRecord[];
  selectedTags: Set<TagRecord["tag_id"]>;
  onTagClick: (prop: { aid: string; tagId: TagRecord["tag_id"] }) => void;
  aid: string;
  taggedTagIds: TagRecord["tag_id"][];
  onAddNewClick: () => void;
};

function AptTagsPanel({
  allAppointmentTags,
  selectedTags,
  onTagClick,
  aid,
  taggedTagIds,
  onAddNewClick,
}: AptTagsPanelProps): JSX.Element {
  const taggedTagIdsSet = React.useMemo(
    () => new Set(taggedTagIds),
    [taggedTagIds]
  );

  const appointmentTags = useMemo(
    () =>
      [...allAppointmentTags].sort((l, r) => {
        let score = 0;
        if (selectedTags.has(l.tag_id) && selectedTags.has(r.tag_id)) {
          score += 0;
        } else if (selectedTags.has(l.tag_id)) {
          score -= 100;
        } else if (selectedTags.has(r.tag_id)) {
          score += 100;
        }

        if (taggedTagIdsSet.has(l.tag_id) && taggedTagIdsSet.has(r.tag_id)) {
          score += 0;
        } else if (taggedTagIdsSet.has(l.tag_id)) {
          score -= 10;
        } else if (taggedTagIdsSet.has(r.tag_id)) {
          score += 10;
        }

        score += l.label.localeCompare(r.label);

        return score;
      }),
    [allAppointmentTags, selectedTags, taggedTagIdsSet]
  );

  if (appointmentTags.length === 0) {
    return <></>;
  }

  return (
    <div className="flex flex-col min-w-208 max-w-208 max-h-304">
      <div className="flex justify-between items-center px-16 py-12">
        <p className="text-13 font-700">Select Tags</p>
      </div>

      <div className="flex flex-col font-700 text-11 overflow-auto">
        {appointmentTags.map((val, idx) => {
          const isSelected = taggedTagIdsSet.has(val.tag_id);
          const {
            label,
            hex: textColor,
            bg_hex: backgroundColor,
            tag_id,
          } = val;

          return (
            <div
              key={val.tag_id}
              className={`flex px-16 py-8 ${
                appointmentTags.length - 1 !== idx
                  ? "border-b border-neutral-9"
                  : ""
              }`}
            >
              <div
                key={`${tag_id}-${isSelected}`}
                onClick={() => {
                  track("BILLING_TAG_AN_APPOINTMENT", {
                    previouslySelected: `${isSelected}`,
                  });
                  onTagClick({ aid, tagId: tag_id });
                }}
                style={
                  isSelected
                    ? {
                        color: textColor,
                        backgroundColor,
                      }
                    : {}
                }
                className={`px-8 py-4 rounded-8 border border-darwin-neutral-100 whitespace-nowrap min-w-max text-darwin-neutral-900 cursor-pointer mr-8`}
              >
                {label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AptTagsPanel;
